import type { ReactNode } from 'react';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { ClassNameProp } from '../common-types';

/**
 * Properties for {@link Tabs} component.
 */
export interface TabsProps extends ClassNameProp {
  /**
   * Individual tab components
   */
  children: ReactNode;
  /**
   * Pass label to set initial active Tab.
   */
  activeTab?: string;
  /**
   * The change callback.
   * @param label the selected Tab label as string.
   */
  onChange?: (label: string) => void;
}

interface TabContextType {
  selectedTab: string | null;
  handleTabClick: (tab: string) => void;
}

export const TabContext = createContext<TabContextType | undefined>(undefined);

/**
 * Component to manage multiple Tab components as a single group using a
 * label value to determine which Tab is active.
 *
 * @return the created {@link JSX.Element}.
 */
export function Tabs({
  className = '',
  ...props
}: Readonly<TabsProps>): JSX.Element {
  const cName = `ehs-tabs 
  ${className ?? ''} `;
  const [selectedTab, setSelectedTab] = useState(props.activeTab ?? '');

  // Function to update the selected children tab
  const handleTabClick = useCallback(
    (activeTab: string) => {
      setSelectedTab(activeTab);
      props.onChange?.(activeTab);
    },
    [props]
  );

  useEffect(() => {
    if (props.activeTab) {
      setSelectedTab(props.activeTab);
    }
  }, [props.activeTab]);

  //prop to the Context provider
  const contextValues = useMemo(
    () => ({
      selectedTab,
      handleTabClick
    }),
    [selectedTab, handleTabClick]
  );

  // Allow using arrowKeys to focus on non-active Tab
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const activeTab = document.activeElement;
    if (e.key === 'ArrowRight' && activeTab) {
      moveFocus(activeTab, +1);
    }
    if (e.key === 'ArrowLeft' && activeTab) {
      moveFocus(activeTab, -1);
    }
  };

  // Depends on arrowKey, focus is put on the correct Tab
  const moveFocus = (activeTab: Element, move: number) => {
    const tabItems = document.querySelectorAll(
      '.ehs-tabs [role="tab"]:not(.disabled)'
    );

    for (let i = 0; i < tabItems.length; i++) {
      const listLength = tabItems.length;

      // If arrowRight, put focus on next right Tab
      if (
        move === 1 &&
        activeTab === tabItems[i] &&
        activeTab !== tabItems[listLength - 1]
      ) {
        (tabItems[i + 1] as HTMLElement)?.focus();
      }

      // If arrowLeft, put focus on next left Tab
      else if (
        move === -1 &&
        activeTab === tabItems[i] &&
        activeTab !== tabItems[0]
      ) {
        (tabItems[i - 1] as HTMLElement)?.focus();
      }
    }
  };

  return (
    <TabContext.Provider value={contextValues}>
      <div className={cName.trim()} role="tablist" onKeyDown={handleKeyDown}>
        {props.children}
      </div>
    </TabContext.Provider>
  );
}
